import React, { useEffect, useState } from 'react'
import { CompareTotalData } from './CompareTotalData'
import { getCompareDataApi, getStatesByKey } from '../core/_requests'
import { MultiValue, SingleValue } from 'react-select'
import { CompanyListItem, CompareData, SelectItem } from '../core/_models'
import { useIntl } from 'react-intl'

type Props = {
  company: SingleValue<SelectItem>
  companySite: MultiValue<CompanyListItem>
  meters: MultiValue<CompanyListItem>
  meterType: string
  readingType: string
  forcedFlipState?: 'energy' | 'carbon'
}

const TotalDataBox: React.FC<Props> = ({
  company,
  companySite,
  meters,
  readingType,
  meterType,
  forcedFlipState,
}) => {
  const [compareData, setCompareData] = useState<CompareData | null>(null)
  const intl = useIntl()
  const [loading, setLoading] = useState(true)
  const [isFlipped, setIsFlipped] = useState(false)
  const [conversionFactor, setConversionFactor] = useState(1)

  useEffect(() => {
    if (forcedFlipState === 'carbon') {
      setIsFlipped(true)
    } else if (forcedFlipState === 'energy') {
      setIsFlipped(false)
    }
  }, [forcedFlipState])

  // Determine API meter type
  let apiMeterType = meterType
  let conversionFactorType = 'conversion_factor_electric'

  if (meterType === 'Gas') {
    apiMeterType = 'Gas'
    conversionFactorType = 'conversion_factor_gas'
  } else if (meterType === 'Electric') {
    apiMeterType = 'Electricity'
    conversionFactorType = 'conversion_factor_electric'
  }

  const params = {
    isLoggedIn: true,
    companyId: company ? company.value : '',
    siteId: companySite,
    metersId: meters,
    meterType: apiMeterType,
    readingType: readingType,
  }

  const fetchCompareData = async () => {
    // Parallel fetch: Data + Conversion Factor
    const dataPromise = getCompareDataApi({ ...params }).then(() => {
      // The original code had a strange double-fetch with 1s delay.
      // Maintaining that logic to be safe, though it looks redundant.
      return new Promise<CompareData>((resolve) => {
        setTimeout(async () => {
          const data = await getCompareDataApi(params)
          resolve(data)
        }, 1000)
      })
    })

    const factorPromise = getStatesByKey(conversionFactorType).then((factor) => {
      return typeof factor === 'number' && !Number.isNaN(factor) ? factor : 1
    })

    const [data, factor] = await Promise.all([dataPromise, factorPromise])
    setCompareData(data)
    setConversionFactor(factor)
    setLoading(false)
  }

  useEffect(() => {
    if (company || companySite || meters) {
      setLoading(true)
      fetchCompareData()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [company, companySite, meters, meterType]) // Added meterType to dependencies

  // Build CO2-converted data
  const co2Data: CompareData | null = compareData
    ? {
        ...compareData,
        current: {
          ...compareData.current,
          total: parseFloat(((compareData.current.total * conversionFactor) / 1000).toFixed(2)),
          avgConsumption: parseFloat(
            ((compareData.current.avgConsumption * conversionFactor) / 1000).toFixed(2)
          ),
        },
        previous: {
          ...compareData.previous,
          total: parseFloat(((compareData.previous.total * conversionFactor) / 1000).toFixed(2)),
          avgConsumption: parseFloat(
            ((compareData.previous.avgConsumption * conversionFactor) / 1000).toFixed(2)
          ),
        },
      }
    : null

  if (loading) {
    return (
      <span className='indicator-progress' style={{ display: 'block', textAlign: 'center' }}>
        {intl.formatMessage({ id: 'LOADING' })}...{' '}
        <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
      </span>
    )
  }

  if (!compareData) return null

  return (
    <div
      style={{
        perspective: '1000px',
        width: '100%',
        height: '100%', // Ensure it takes full height of column
      }}
    >
      <div
        style={{
          position: 'relative',
          width: '100%',
          height: '100%',
          transition: 'transform 0.6s',
          transformStyle: 'preserve-3d',
          transform: isFlipped ? 'rotateY(180deg)' : 'rotateY(0deg)',
        }}
      >
        {/* Front Face (Energy) */}
        <div
          style={{
            position: 'absolute', // Absolute positioning for flip faces
            width: '100%',
            height: '100%',
            backfaceVisibility: 'hidden',
            WebkitBackfaceVisibility: 'hidden', // Safari support
            transform: 'rotateY(0deg)',
            zIndex: isFlipped ? 1 : 2,
            top: 0,
            left: 0,
          }}
        >
          <CompareTotalData
            className='card-xl-stretch mb-xl-8'
            chartHeight='150px'
            data={compareData}
            readingType={readingType}
            type={apiMeterType}
            showFlipButton={true}
            onFlip={() => setIsFlipped(true)}
          />
        </div>

        {/* Back Face (CO2) */}
        <div
          style={{
            position: 'absolute', // Absolute positioning for flip faces
            width: '100%',
            height: '100%',
            backfaceVisibility: 'hidden',
            WebkitBackfaceVisibility: 'hidden', // Safari support
            transform: 'rotateY(180deg)',
            zIndex: isFlipped ? 2 : 1,
            top: 0,
            left: 0,
          }}
        >
          {co2Data && (
            <CompareTotalData
              className='card-xl-stretch mb-xl-8'
              chartHeight='150px'
              data={co2Data}
              readingType={readingType}
              type='co2'
              showFlipButton={true}
              onFlip={() => setIsFlipped(false)}
            />
          )}
        </div>
      </div>
      {/* Spacer to maintain height in flow since children are absolute */}
      <div style={{ visibility: 'hidden' }}>
        <CompareTotalData
          className='card-xl-stretch mb-xl-8'
          chartHeight='150px'
          data={compareData}
          readingType={readingType}
          type={apiMeterType}
        />
      </div>
    </div>
  )
}

export default TotalDataBox
